/**
 * FaqTab
 * Sökbar FAQ-lista i ChatWidget
 */

import React, { useState, useMemo } from 'react';
import { MagnifyingGlassIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import type { FaqItem } from '@/types/chat';

interface FaqTabProps {
  faqItems: FaqItem[];
  t: (key: string) => string;
}

const FaqTab: React.FC<FaqTabProps> = ({ faqItems, t }) => {
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return faqItems;
    return faqItems.filter(
      (f) =>
        f.question.toLowerCase().includes(q) || f.answer.toLowerCase().includes(q)
    );
  }, [faqItems, search]);

  if (faqItems.length === 0) {
    return (
      <div className="p-6 text-center text-textSecondary">
        <p className="text-sm">{t('chat.noFaqYet') || 'Inga FAQ tillagda ännu'}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="p-3 border-b border-border">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-textSecondary" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('chat.faqSearchPlaceholder') || 'Sök i vanliga frågor...'}
            className="w-full pl-10 pr-3 py-2 border border-border rounded-lg bg-bgBase text-textDefault text-sm"
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto divide-y divide-border">
        {filtered.length === 0 ? (
          <div className="p-6 text-center text-textSecondary">
            <p className="text-sm">{t('chat.noFaqFound') || 'Inga träffar'}</p>
          </div>
        ) : (
          filtered.map((item) => {
            const isExpanded = expandedId === item.faq_id;
            return (
              <div
                key={item.faq_id}
                className="border-b border-border last:border-b-0"
              >
                <button
                  onClick={() =>
                    setExpandedId(isExpanded ? null : item.faq_id)
                  }
                  className="w-full p-3 text-left hover:bg-bgContainer transition-colors flex items-start gap-2"
                >
                  {isExpanded ? (
                    <ChevronDownIcon className="w-4 h-4 text-textSecondary mt-0.5 flex-shrink-0" />
                  ) : (
                    <ChevronRightIcon className="w-4 h-4 text-textSecondary mt-0.5 flex-shrink-0" />
                  )}
                  <span className="font-medium text-textDefault text-sm flex-1">
                    {item.question}
                  </span>
                </button>
                {isExpanded && (
                  <div className="px-3 pb-3 pl-9">
                    <p className="text-sm text-textSecondary whitespace-pre-wrap">
                      {item.answer}
                    </p>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default FaqTab;
